import React, { useEffect, useState, useCallback } from "react";
import api from "../api";
import WebApp from "@twa-dev/sdk";
import { Dropdown } from "rsuite";
import { DateRangePicker } from "rsuite";
import { useMainButton } from '../hooks/useMainButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

import "rsuite/Dropdown/styles/index.css";
import "rsuite/DateRangePicker/styles/index.css";

const { allowedRange } = DateRangePicker;

const ReportPage = () => {
  const [feeds, setFeeds] = useState([]);
  const [selectedFeed, setSelectedFeed] = useState("Тема отчета");
  const [reportFormat, setReportFormat] = useState('word');
  const [dateRange, setDateRange] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const PickerLocale = {
    sunday: "Вс",
    monday: "Пн",
    tuesday: "Вт",
    wednesday: "Ср",
    thursday: "Чт",
    friday: "Пт",
    saturday: "Сб",
    ok: "OK",
    today: "Сегодня",
    yesterday: "Вчера",
    hours: "Часы",
    minutes: "Минуты",
    seconds: "Секунды",
    title: "Выберите дату",
    placeholder: "Выберите дату",
    last7Days: "Последние 7 дней",
  };

  const isFormComplete = selectedFeed !== "Тема отчета" && dateRange.length > 0;

  const handleReportRequest = useCallback(async () => {
    if (selectedFeed === "Тема отчета") {
      WebApp.showAlert("Выберите тему отчета");
      return;
    }
    if (dateRange.length === 0) {
      WebApp.showAlert("Выберите период отчета");
      return;
    }
    setDisabled(true);
    try {
      const formatDateForMoscow = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      const requestDateFrom = formatDateForMoscow(dateRange[0]);
      const requestDateTo = formatDateForMoscow(dateRange[1]);
      const response = await api.get('/api/report/generate', {
        params: {
          feed: selectedFeed,
          startDate: requestDateFrom,
          endDate: requestDateTo,
          report_format: reportFormat
        }
      });
      if (response.status === 200) {
        WebApp.HapticFeedback.notificationOccurred('success');
        WebApp.showAlert("Отчет успешно сформирован и отправлен в диалог");
      } else {
        WebApp.HapticFeedback.notificationOccurred('error');
        WebApp.showAlert("Ошибка при формировании отчета");
      }
    } catch (error) {
      WebApp.HapticFeedback.notificationOccurred('error');
      WebApp.showAlert("Ошибка при формировании отчета");
    } finally {
      setDisabled(false);
    }
  }, [selectedFeed, dateRange, reportFormat]);

  useMainButton({
    text: 'Запросить отчет',
    onClick: handleReportRequest,
    visible: true,
    disabled: !isFormComplete || disabled,
    showProgress: disabled
  });

  const fetchFeeds = useCallback(() => {
    setLoading(true);
    setFetchError(false);
    api.get("/api/feed/all")
      .then((response) => {
        setFeeds(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("There was an error!", error);
        setFetchError(true);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchFeeds();
  }, [fetchFeeds]);

  const twoYearsAgo = new Date();
  twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
  const minDateStr = `${twoYearsAgo.getFullYear()}-${String(twoYearsAgo.getMonth() + 1).padStart(2, '0')}-${String(twoYearsAgo.getDate()).padStart(2, '0')}`;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <FontAwesomeIcon icon={faSpinner} spin className="text-tg-hint text-2xl" />
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <p className="text-tg-hint">Не удалось загрузить ленты</p>
        <button onClick={fetchFeeds} className="py-2 px-4 rounded-lg bg-tg-button text-tg-button-text border-none cursor-pointer">
          Повторить
        </button>
      </div>
    );
  }

  if (feeds.length === 0) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-tg-hint">Нет доступных лент</p>
      </div>
    );
  }

  return (
    <div className="p-4 bg-tg-bg text-tg-text">
      <div className="m-4 mx-auto max-w-md rounded-xl shadow-md md:max-w-2xl bg-tg-bg text-tg-text">
        <div className="md:flex">
          <div className="px-5 pt-5">
            <Dropdown title={selectedFeed}>
              {feeds.map((feed) => (
                <Dropdown.Item
                  key={feed.name}
                  onClick={() => setSelectedFeed(feed.name)}
                >
                  {feed.name}
                </Dropdown.Item>
              ))}
            </Dropdown>
          </div>
        </div>
        <div className="px-5 py-4">
          <Dropdown title={reportFormat}>
            <Dropdown.Item onClick={() => setReportFormat('word')}>Word</Dropdown.Item>
            <Dropdown.Item onClick={() => setReportFormat('excel')}>Excel</Dropdown.Item>
          </Dropdown>
        </div>
        <div className="px-5 pb-5">
          <DateRangePicker
            showOneCalendar
            placeholder="Период отчета"
            format="dd.MM.yyyy"
            character=" — "
            locale={PickerLocale}
            isoWeek={true}
            shouldDisableDate={allowedRange(minDateStr, Date())}
            onOk={(value) => setDateRange(value)}
          />
        </div>
      </div>
    </div>
  );
};

export default ReportPage;
